import { Label } from './Label.js';
import type { Status } from './Status.js';
import type { Tuple } from '../types/index.js';

export type Message = string | Message.TemplateFunctionArgs;

export namespace Message {
  export function format(
    message: Message,
    status: Status | null,
    labels: Message.Labels
  ): readonly [string, ...ReadonlyArray<unknown>] {
    const [statusLabel, messageColor] = status ?? [[''], null];
    const [statusLabelText, ...statusLabelArgs] = statusLabel;
    const [labelsText, labelsArgs] = labels;

    const templateFunctionArgs: TemplateFunctionArgs =
      typeof message === 'string'
        ? [[message] as unknown as TemplateStringsArray]
        : message;

    const [templateStrings, ...templateValues] = templateFunctionArgs;
    const messageText = `%c${templateStrings.join('%o')}`;

    const messageArgs = [
      `font-weight: normal;${messageColor ? ` color: rgb(${messageColor.join(' ')});` : ''}`,
      ...templateValues
    ];

    return [
      `${statusLabelText}${labelsText} ${messageText}`,
      ...statusLabelArgs,
      ...labelsArgs,
      ...messageArgs
    ] as const;
  }

  export type Args<TData extends Tuple.Any> = readonly [
    message: string,
    ...data: TData
  ];

  export type TemplateFunctionArgs = readonly [
    templateStrings: TemplateStringsArray,
    ...values: ReadonlyArray<unknown>
  ];

  export enum Type {
    Log = 'log',
    Info = 'info',
    Debug = 'debug',
    Warn = 'warn',
    Error = 'error'
  }

  export namespace Type {
    export type Groupable = Type.Log | Type.Info;

    export type Expanded = Type.Debug | Type.Warn | Type.Error;
  }

  export enum Grouping {
    Group = 'group',
    GroupCollapsed = 'groupCollapsed',
    NewLines = 'newLines',
    Ungrouped = 'ungrouped'
  }

  export namespace Grouping {
    export type Grouped = Grouping.Group | Grouping.GroupCollapsed;

    export type Expanded = Grouping.NewLines | Grouping.Ungrouped;

    export type FromType<TType extends Type> = TType extends Type.Groupable
      ? Grouping
      : Grouping.Expanded;
  }

  export type Labels = readonly [string, ReadonlyArray<string>];

  export namespace Labels {
    export function reducer(
      [resultText, resultArgs]: Message.Labels,
      { text, textColor, backgroundColor }: Label
    ): Message.Labels {
      return [
        `${resultText}%c ${text} %c`,
        [
          ...resultArgs,
          `color: rgb(${textColor.join(' ')}); background: rgb(${backgroundColor.join(' ')}); font-weight: normal;`,
          ''
        ]
      ] as const;
    }

    export function from(labels: ReadonlyArray<Label>) {
      return [Label.BASE, ...labels].reduce(Message.Labels.reducer, ['', []]);
    }
  }
}
